import { useState, useEffect, useRef } from 'react';
import { fetchCptCodes } from '../services/api';
import { resolveLineCharge, isSplitBillable } from '../utils/billing';
import './CptCodeCombobox.css';

/**
 * Typeahead for CPT/HCPCS codes on the claim service lines. Renders the CPT input plus a
 * dropdown of matching codes from the facility fee schedule, with this facility's most-used
 * codes grouped under "Frequently used" on top. Picking a code auto-fills the line's
 * description and charge.
 *
 * - onCodeChange(code): free-text edits to the CPT field.
 * - onPick(code, { description, charge, splitBillable }, { explicit }): a code was resolved.
 *     explicit=true  → user clicked/entered a suggestion (parent fills description + charge).
 *     explicit=false → exact-match resolved on blur (parent fills only empty fields).
 *   charge is a number or null when the code has no price.
 *   splitBillable is true/false/null (null = code not on the fee schedule).
 * - component: the claim's billing component ('professional' | 'technical' | 'global').
 *     Selects WHICH price is offered — a technical claim must fill the TC charge, not the
 *     global one, which is the same amount for a global claim but 60-80% of it for TC.
 */
function CptCodeCombobox({ code, facilityId, component = 'global', onCodeChange, onPick, onOpenChange, disabled, inputClassName = 'gc-input', placeholder = 'Code' }) {
  const [open, setOpenState]      = useState(false);
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const blurTimer = useRef(null);
  const openRef   = useRef(false);

  // Wrap open state so a parent can reserve room for the dropdown (some form cards clip
  // overflow). The ref dedupes notifications so the parent's open-counter stays balanced.
  const setOpen = (next) => {
    if (openRef.current === next) return;
    openRef.current = next;
    setOpenState(next);
    onOpenChange?.(next);
  };

  // Debounced fetch while the dropdown is open. Re-queries as the biller types.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      fetchCptCodes({ q: code, facilityId })
        .then((res) => { if (active) setResults(res.data || []); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 180);
    return () => { active = false; clearTimeout(t); };
  }, [open, code, facilityId]);

  useEffect(() => () => {
    clearTimeout(blurTimer.current);
    if (openRef.current) onOpenChange?.(false);
  }, []);

  // Effective values: a facility's overrides win over the standard reference values, and the
  // charge is the one matching the component this claim bills.
  const descOf   = (item) => item.custom_description || item.description;
  const chargeOf = (item) => resolveLineCharge(item, component);

  // `frequent` is computed server-side: (pinned OR used) AND NOT hidden.
  const frequent = results.filter((r) => r.frequent);
  const others   = results.filter((r) => !r.frequent);
  const ordered  = [...frequent, ...others];

  const pick = (item, explicit) => {
    onPick(
      item.code,
      { description: descOf(item), charge: chargeOf(item), splitBillable: isSplitBillable(item.pc_tc_indicator) },
      { explicit }
    );
    setOpen(false);
    setHighlight(-1);
  };

  const handleBlur = () => {
    // Delay so an option's onMouseDown fires first.
    blurTimer.current = setTimeout(() => {
      setOpen(false);
      setHighlight(-1);
      // Auto-resolve a fully-typed code to its fee-schedule values on the way out.
      const typed = (code || '').trim();
      if (typed) {
        const match = results.find((r) => r.code === typed);
        if (match) {
          onPick(
            match.code,
            { description: descOf(match), charge: chargeOf(match), splitBillable: isSplitBillable(match.pc_tc_indicator) },
            { explicit: false }
          );
        }
      }
    }, 120);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, ordered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && ordered[highlight]) { e.preventDefault(); pick(ordered[highlight], true); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const fmtCharge = (c) => (c != null && c !== '' ? `$${Number(c).toFixed(2)}` : '');

  const renderOption = (item, idx) => (
    <button
      type="button"
      key={item.code}
      className={`ccc-option ${idx === highlight ? 'ccc-option-active' : ''}`}
      onMouseDown={(e) => { e.preventDefault(); pick(item, true); }}
      onMouseEnter={() => setHighlight(idx)}
    >
      <span className="ccc-option-code">{item.code}</span>
      <span className="ccc-option-desc">{descOf(item)}</span>
      {chargeOf(item) != null && <span className="ccc-option-charge">{fmtCharge(chargeOf(item))}</span>}
      {item.pinned ? (
        <svg className="ccc-option-pin" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-label="Pinned">
          <path d="M14 4V2H7v2h1v6l-2 2v2h5v6h2v-6h5v-2l-2-2V4z" />
        </svg>
      ) : null}
    </button>
  );

  return (
    <div className="ccc-wrap">
      <input
        className={inputClassName}
        type="text"
        value={code}
        onChange={(e) => { onCodeChange(e.target.value); if (!open) setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={5}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div className="ccc-dropdown">
          {loading ? (
            <div className="ccc-msg">Loading…</div>
          ) : ordered.length === 0 ? (
            <div className="ccc-msg">No matching CPT/HCPCS codes</div>
          ) : (
            <>
              {frequent.length > 0 && <div className="ccc-group-label">Frequently used at this facility</div>}
              {frequent.map((item, i) => renderOption(item, i))}
              {others.length > 0 && frequent.length > 0 && <div className="ccc-group-label">All codes</div>}
              {others.map((item, i) => renderOption(item, frequent.length + i))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CptCodeCombobox;
